import { create } from "zustand";
import { devtools } from "zustand/middleware";

import {
  createProject as apiCreateProject,
  deleteProject as apiDeleteProject,
  getProjectStudies,
  getProjectsWithStudies,
} from "../features/workspace/services/workspaceService";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
let workspaceLoadPromise = null;
let workspaceLoadGeneration = 0;

export const useWorkspaceStore = create(
  devtools(
    (set, get) => ({
      projects: [],
      studies: [],
      recentArtifacts: [],
      currentProject: null,
      currentStudy: null,
      projectsLoading: false,
      studiesLoading: false,
      projectsLoadInFlight: false,
      workspaceLoaded: false,
      createStudyRequests: new Map(),

      setProjects: (projects) => set({ projects }, false, "workspace/setProjects"),
      setStudies: (studies) => set({ studies }, false, "workspace/setStudies"),
      setCurrentProject: (currentProject) =>
        set({ currentProject }, false, "workspace/setCurrentProject"),
      setCurrentStudy: (currentStudy) =>
        set({ currentStudy }, false, "workspace/setCurrentStudy"),

      resetWorkspace: () => {
        workspaceLoadGeneration += 1;
        workspaceLoadPromise = null;
        set(
          {
            projects: [],
            studies: [],
            recentArtifacts: [],
            currentProject: null,
            currentStudy: null,
            projectsLoading: false,
            studiesLoading: false,
            projectsLoadInFlight: false,
            workspaceLoaded: false,
            createStudyRequests: new Map(),
          },
          false,
          "workspace/reset"
        );
      },

      loadProjects: async ({ force = false } = {}) => {
        const current = get();
        if (!force && current.workspaceLoaded) return current.projects;
        if (current.projectsLoadInFlight && workspaceLoadPromise) return workspaceLoadPromise;
        set(
          { projectsLoadInFlight: true, projectsLoading: true },
          false,
          "workspace/loadProjectsStart"
        );

        const generation = workspaceLoadGeneration;
        const request = (async () => {
          try {
            let result = null;
            let lastError = null;
            for (let attempt = 0; attempt < 3; attempt += 1) {
              try {
                // eslint-disable-next-line no-await-in-loop
                result = await getProjectsWithStudies();
                lastError = null;
                break;
              } catch (error) {
                lastError = error;
                // eslint-disable-next-line no-await-in-loop
                await sleep(attempt === 0 ? 250 : attempt === 1 ? 700 : 1400);
              }
            }
            if (!result && lastError) throw lastError;
            if (generation !== workspaceLoadGeneration) return null;

            if (result.success) {
              const projects = result.projects || [];
              const studies = result.all_studies || [];
              const recentArtifacts = result.recent_artifacts || [];
              set(
                { projects, studies, recentArtifacts, workspaceLoaded: true },
                false,
                "workspace/loadProjectsSuccess"
              );
              console.log(
                "📚 프로젝트 및 스터디 로드 완료:",
                projects.length,
                "개 프로젝트,",
                studies.length,
                "개 스터디,",
                recentArtifacts.length,
                "개 최근 아티팩트"
              );
              return projects;
            }
            console.error("프로젝트 로드 실패:", result.error);
            return null;
          } catch (error) {
            if (generation === workspaceLoadGeneration) {
              console.error("프로젝트 로드 실패:", error);
            }
            return null;
          } finally {
            if (generation === workspaceLoadGeneration) {
              set(
                { projectsLoadInFlight: false, projectsLoading: false },
                false,
                "workspace/loadProjectsEnd"
              );
            }
          }
        })();
        workspaceLoadPromise = request;
        try {
          return await request;
        } finally {
          if (workspaceLoadPromise === request) workspaceLoadPromise = null;
        }
      },

      createProject: async (projectData) => {
        try {
          const result = await apiCreateProject(projectData);
          if (result.success && result.project) {
            set(
              (state) => ({ projects: [result.project, ...state.projects] }),
              false,
              "workspace/createProjectSuccess"
            );
            return result.project;
          }
          throw new Error(result.error || "프로젝트 생성 실패");
        } catch (error) {
          console.error("프로젝트 생성 오류:", error);
          alert(error.message);
          return null;
        }
      },

      getProject: (projectIdOrSlug) => {
        if (!projectIdOrSlug) return undefined;
        return get().projects.find((project) => {
          const matchesId = String(project.id) === String(projectIdOrSlug);
          const matchesSlug = project.slug && project.slug === projectIdOrSlug;
          return matchesId || matchesSlug;
        });
      },

      updateProject: (projectId, updates) => {
        set(
          (state) => ({
            projects: state.projects.map((project) =>
              String(project.id) === String(projectId)
                ? { ...project, ...updates, last_modified: new Date().toISOString() }
                : project
            ),
          }),
          false,
          "workspace/updateProject"
        );
      },

      deleteProject: async (projectId) => {
        try {
          const result = await apiDeleteProject(projectId);
          if (result.success) {
            set(
              (state) => ({
                projects: state.projects.filter(
                  (project) => String(project.id) !== String(projectId)
                ),
                currentProject:
                  state.currentProject &&
                  String(state.currentProject.id) === String(projectId)
                    ? null
                    : state.currentProject,
                studies: state.studies.filter(
                  (study) => String(study.project_id) !== String(projectId)
                ),
              }),
              false,
              "workspace/deleteProjectSuccess"
            );
          }
        } catch (error) {
          console.error("프로젝트 삭제 오류:", error);
          alert(error.message);
        }
      },

      loadStudiesByProject: async (projectId) => {
        console.log("workspaceStore: loadStudiesByProject 시작, projectId:", projectId);
        set({ studiesLoading: true }, false, "workspace/loadStudiesStart");
        try {
          const result = await getProjectStudies(projectId);
          console.log("workspaceStore: API 응답:", result);
          if (result.success) {
            console.log("workspaceStore: 로드된 조사 목록:", result.studies);
            set(
              { studies: result.studies || [] },
              false,
              "workspace/loadStudiesSuccess"
            );
          } else {
            console.error("조사 목록 로드 실패:", result.error);
            set({ studies: [] }, false, "workspace/loadStudiesFailure");
          }
        } catch (error) {
          console.error("조사 로드 실패:", error);
          set({ studies: [] }, false, "workspace/loadStudiesError");
        } finally {
          set({ studiesLoading: false }, false, "workspace/loadStudiesEnd");
          console.log("workspaceStore: loadStudiesByProject 완료");
        }
      },

      createStudy: async (projectId, studyData = {}) => {
        if (!projectId) {
          console.error("조사 생성 오류: projectId가 필요합니다.");
          return null;
        }
        if (!studyData.name) {
          alert("조사 이름은 필수입니다.");
          return null;
        }

        const dedupKey = [
          String(projectId),
          (studyData.name || "").trim().toLowerCase(),
          (studyData.description || "").trim(),
        ].join("::");

        const pendingRequests = get().createStudyRequests;
        if (pendingRequests.has(dedupKey)) {
          return pendingRequests.get(dedupKey);
        }

        const createPromise = (async () => {
          try {
            const newStudy = {
              id: Date.now(),
              project_id: projectId,
              name: studyData.name,
              initial_input: studyData.description || "",
              created_at: new Date().toISOString(),
            };
            set(
              (state) => ({ studies: [newStudy, ...state.studies] }),
              false,
              "workspace/createStudy"
            );
            return newStudy;
          } catch (error) {
            console.error("조사 생성 오류:", error);
            alert(error?.message || "조사 생성에 실패했습니다.");
            return null;
          }
        })();

        set(
          (state) => {
            const nextRequests = new Map(state.createStudyRequests);
            nextRequests.set(dedupKey, createPromise);
            return { createStudyRequests: nextRequests };
          },
          false,
          "workspace/createStudyRequestStart"
        );
        try {
          return await createPromise;
        } finally {
          set(
            (state) => {
              const nextRequests = new Map(state.createStudyRequests);
              nextRequests.delete(dedupKey);
              return { createStudyRequests: nextRequests };
            },
            false,
            "workspace/createStudyRequestEnd"
          );
        }
      },

      getStudy: (studyId) =>
        get().studies.find((study) => String(study.id) === String(studyId)),

      getStudiesByProject: (projectId) =>
        get().studies.filter(
          (study) => String(study.project_id) === String(projectId)
        ),

      updateStudy: (studyId, updates) => {
        set(
          (state) => ({
            studies: state.studies.map((study) =>
              study.id === studyId
                ? {
                    ...study,
                    ...updates,
                    lastModified: new Date().toISOString().split("T")[0],
                  }
                : study
            ),
          }),
          false,
          "workspace/updateStudy"
        );
      },

      deleteStudy: (studyId) => {
        set(
          (state) => ({
            studies: state.studies.filter((study) => study.id !== studyId),
            currentStudy:
              state.currentStudy && state.currentStudy.id === studyId
                ? null
                : state.currentStudy,
          }),
          false,
          "workspace/deleteStudy"
        );
      },
    }),
    { name: "workspace-store" }
  )
);
